/**
 * Shared in-memory task store for local/offline use, routed in by TaskUtils
 * when mock mode is on (see Knowledge/mockMode `isMockMode`). The Tasks page
 * and the Hero Trainer Center share this one store; state resets on a full
 * page reload.
 *
 * Dev override: localStorage.bbvr_mock_tasks = 'none' answers every task read
 * with an empty list, which is how the all-done and wrap-up states are seen.
 *
 * The Knowledge store is imported on demand: TaskUtils rides in the main chunk
 * (activeFlows) and the seeded articles must not ship with it.
 */

import { isMockMode } from '../Knowledge/mockMode';
import { getUserName } from '../Utils/GeneralUtils';
import { TESTIMONIAL_TARGET, normalizeVerification, parseFlowKind, parseFlowOrder } from './taskVerification';

export { isMockMode };

const NETWORK_DELAY_MS = 130;
const delay = (value) => new Promise((resolve) => setTimeout(() => resolve(value), NETWORK_DELAY_MS));

const dateOnly = (daysFromNow = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return d.toISOString().slice(0, 10);
};

const todayAt = (hour, minute) => {
    const d = new Date();
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
};

const verification = (method, overrides = {}) => normalizeVerification({ method, ...overrides });

const me = () => {
    try {
        return getUserName() || 'Demo Operator';
    } catch (e) {
        return 'Demo Operator';
    }
};

let store = null;
let seq = 0;
const newTaskId = () => `mock-tk-${Date.now()}-${(seq += 1)}`;

const seed = () => {
    const user = me();
    const poolNote = 'Shared shift pool — everyone on staff sees this. Only check off for your own shift.';
    return [
        { taskId: 'tk-1', id: user, taskName: 'Reset VR booth 3 controllers', description: 'Booth 3 controllers were drifting last night — re-pair and test each one.', status: 'New', priority: 'High', dueDate: dateOnly(-1), startDate: dateOnly(-1), assignedTo: user, assignedBy: 'Jordan (Manager)', recurrence: null, shiftPhase: '', requirementLevel: 'required' },
        { taskId: 'tk-2', id: user, taskName: 'Submit incident follow-up notes', description: "Add detail to yesterday's slip report before it's archived.", status: 'New', priority: 'Medium', dueDate: dateOnly(-2), startDate: dateOnly(-2), assignedTo: user, assignedBy: 'Jordan (Manager)', recurrence: null, shiftPhase: '', requirementLevel: 'required' },
        { taskId: 'tk-3', id: user, taskName: 'Pre-open safety walk', description: poolNote + ' Open shift — required at start.', status: 'New', priority: 'High', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'Jordan (Manager)', recurrence: 'daily', shiftPhase: 'open', requirementLevel: 'required' },
        { taskId: 'tk-4', id: user, taskName: 'Restock waivers at front desk', description: 'Print 50 and refill the clipboard.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(0), assignedTo: user, assignedBy: 'Maria Gomez', recurrence: null, shiftPhase: '', requirementLevel: 'required' },
        { taskId: 'tk-5', id: user, taskName: 'Wipe down headsets between sessions', description: poolNote + ' All day — if you have time.', status: 'In Progress', priority: 'Low', dueDate: dateOnly(0), startDate: dateOnly(-10), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'all_day', requirementLevel: 'optional' },
        { taskId: 'tk-6', id: user, taskName: 'Morning huddle', description: 'Quick team sync on the day’s events.', status: 'Completed', completionOutcome: 'done', completedBy: user, completedAt: todayAt(7, 5), priority: 'Low', dueDate: dateOnly(0), startDate: dateOnly(0), assignedTo: user, assignedBy: 'Jordan (Manager)', recurrence: 'daily', shiftPhase: 'open', requirementLevel: 'required' },
        { taskId: 'tk-7', id: 'Maria Gomez', taskName: 'Audit lost & found', description: 'Log and store unclaimed items.', status: 'New', priority: 'Low', dueDate: dateOnly(-1), startDate: dateOnly(-1), assignedTo: 'Maria Gomez', assignedBy: 'Jordan (Manager)', recurrence: 'weekly', shiftPhase: 'all_day', requirementLevel: 'optional' },
        { taskId: 'tk-8', id: 'Devin Brooks', taskName: 'Update booth signage', description: 'Swap in the new pricing posters.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(0), assignedTo: 'Devin Brooks', assignedBy: 'Maria Gomez', recurrence: null, shiftPhase: '', requirementLevel: 'required' },
        { taskId: 'tk-9', id: user, taskName: 'Quarterly safety recertification', description: 'Complete the online recert module before it lapses.', status: 'New', priority: 'Medium', dueDate: dateOnly(5), startDate: dateOnly(5), assignedTo: user, assignedBy: 'Jordan (Manager)', recurrence: null, shiftPhase: '', requirementLevel: 'required' },
        { taskId: 'tk-10', id: user, taskName: 'Close: shutdown booths', description: poolNote + ' Close shift — required at end.', status: 'New', priority: 'High', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'close', requirementLevel: 'required', knowledgeArticleId: 'sop-booth-shutdown', verification: verification('headsets_off', { afterLastMember: true }), flowOrder: 1 },
        { taskId: 'tk-11', id: user, taskName: 'Close: plug in iPad 14', description: 'Cable in the port, screen showing charge.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'close', requirementLevel: 'required', knowledgeArticleId: 'sop-gym-closing', verification: verification('photo', { photoPrompt: 'iPad 14 with the charging cable plugged in and the screen showing charge', checkPrompt: 'Confirm an iPad is visible, a charging cable is plugged into it, and the screen or indicator shows it is charging. Do not judge anything else.' }), flowOrder: 2 },
        { taskId: 'tk-12', id: user, taskName: 'Close: mop as needed', description: 'Spot-mop spills and the entry mat.', status: 'New', priority: 'Low', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'close', requirementLevel: 'optional', knowledgeArticleId: 'sop-mopping', verification: verification('confirm'), flowOrder: 4 },
        { taskId: 'tk-13', id: user, taskName: 'Close: wipe furniture and HT station', description: 'Tables, chairs, HT desk.', status: 'Completed', completionOutcome: 'done', completedBy: 'Alex Rivera', completedAt: todayAt(19, 52), priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'close', requirementLevel: 'required', knowledgeArticleId: 'sop-cleaning-furniture', verification: verification('confirm'), flowOrder: 5 },
        { taskId: 'tk-14', id: user, taskName: 'Close: arm alarm, lock up', description: 'Keypad shows ARMED before the door closes.', status: 'New', priority: 'High', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'close', requirementLevel: 'required', knowledgeArticleId: 'sop-gym-closing', verification: verification('confirm', { afterLastMember: true }), flowOrder: 7 },
        { taskId: 'tk-15', id: user, taskName: 'Open: boot booths', description: poolNote + ' Every booth PC on and headset connected.', status: 'New', priority: 'High', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'open', requirementLevel: 'required', knowledgeArticleId: 'sop-gym-opening', verification: verification('headsets_on'), flowOrder: 1 },
        { taskId: 'tk-16', id: user, taskName: 'Open: bathrooms check', description: 'Stocked, wiped, bins empty.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'On Shift Staff', recurrence: 'daily', shiftPhase: 'open', requirementLevel: 'required', knowledgeArticleId: 'sop-cleaning-bathrooms', verification: verification('photo', { photoPrompt: 'One wide shot of the bathroom from the door', photoPolicy: 'optional', sampleRate: 0.5 }), flowOrder: 2 },
        { taskId: 'tk-17', id: user, taskName: 'Deep clean booth 1', description: 'Full wipe-down inside and out, lenses last.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-7), assignedTo: 'On Shift Staff', assignedBy: 'Jordan (Manager)', recurrence: 'weekly', shiftPhase: 'all_day', requirementLevel: 'required', flowKind: 'cleaning', knowledgeArticleId: 'sop-deep-cleaning-a-booth', verification: verification('photo', { photoPrompt: 'Booth 1 interior with the headset on its hook' }), flowOrder: 2 },
        // Weekly deep cleans run in the cleaning flow, and this one links a
        // guided SOP so the step checklist is exercisable offline.
        { taskId: 'tk-18', id: user, taskName: 'Deep clean a bathroom', description: 'Full weekly bathroom clean, top to bottom.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-7), assignedTo: 'On Shift Staff', assignedBy: 'Jordan (Manager)', recurrence: 'weekly', shiftPhase: 'all_day', requirementLevel: 'required', flowKind: 'cleaning', knowledgeArticleId: 'sop-deep-cleaning-a-bathroom', verification: verification('confirm', { requireSopSteps: true }), flowOrder: 1 },
        { taskId: 'seed-member-testimonials', id: user, taskName: 'Get 2+ member testimonials', description: 'Two member quotes or videos per shift. Each one is posted to the managers channel.', status: 'New', priority: 'Medium', dueDate: dateOnly(0), startDate: dateOnly(-30), assignedTo: 'On Shift Staff', assignedBy: 'Jordan (Manager)', recurrence: 'daily', shiftPhase: 'all_day', requirementLevel: 'required', verification: verification('testimonial'), testimonialCount: 0, testimonialTarget: TESTIMONIAL_TARGET },
    ];
};

/** Drops the in-memory store so the next read reseeds it; for tests that check tasks off. */
export const resetMockTasks = () => {
    store = null;
};

const getStore = () => {
    if (!store) store = seed();
    return store;
};

/**
 * A linked article with steps rides along so the task's SOP checklist can
 * render offline, the way TaskBoard.get_assignee_tasks/get_all_tasks attach it
 * server-side. Same lookup mockShiftFlow uses for a flow step's `sop`.
 */
const loadArticleSteps = () => import('../Knowledge/mockKnowledgeData').then((m) => m.mockLoadArticleSteps);

const withSop = async (tasks) => {
    const stepsOf = await loadArticleSteps();
    return tasks.map((task) => ({ ...task, sop: stepsOf((task.knowledgeArticleId || '').trim()) }));
};

const TASKS_OVERRIDE_KEY = 'bbvr_mock_tasks';

const tasksOverride = () => {
    try {
        return localStorage.getItem(TASKS_OVERRIDE_KEY) || '';
    } catch (e) {
        return '';
    }
};

export const mockFetchTasks = async (viewMode, username) => {
    if (tasksOverride() === 'none') return delay([]);
    const tasks = getStore();
    if (viewMode === 'assigned') {
        const who = username || me();
        return delay(await withSop(tasks.filter((t) => t.assignedTo === who || t.assignedTo === 'On Shift Staff')));
    }
    return delay(await withSop(tasks));
};

const slug = (name) => String(name || 'trainer').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Same key layout as TaskEvidence.py so the review tab's mock thumbnails look real. */
export const mockGetEvidenceUploadUrl = (taskId, gymId) => {
    const uuid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const photoKey = `${gymId}/${dateOnly(0)}/${slug(me())}/${taskId}/${uuid}.jpg`;
    return delay({
        uploadUrl: `https://acc-task-evidence.mock/${photoKey}?X-Amz-Signature=mock`,
        photoKey,
        contentType: 'image/jpeg',
        expiresIn: 300,
    });
};

// Verdicts rotate so one offline walk shows every chip the trainer can see.
const MOCK_VERDICTS = [
    { status: 'verified', confidence: 0.94 },
    { status: 'flagged', reason: 'uncertain', issues: ['Cable not visible'], confidence: 0.41 },
    { status: 'recorded' },
];
let verdictSeq = 0;

const mockVerification = (task, meta) => {
    const photoPolicy = task.verification?.photoPolicy || 'required';
    // TaskEvidence.attach records a skip as the verification itself; there is no
    // reason field on it, the trainer's own skip reason is on the completion.
    if (!meta?.photoKey) {
        return { status: 'skipped', method: 'photo', photoPolicy, checkedAt: new Date().toISOString() };
    }
    const verdict = MOCK_VERDICTS[verdictSeq % MOCK_VERDICTS.length];
    verdictSeq += 1;
    if (verdict.status === 'recorded') return { ...verdict, photoPolicy };
    return { ...verdict, model: 'mock-vision', photoPolicy };
};

/**
 * Same rejection TaskBoard._sop_checklist raises: a done completion of a task
 * whose manager turned the checklist into a hard gate must name every required
 * step id. The checklist is a guide unless `requireSopSteps` is explicitly
 * true; Not needed / blocked skip it either way.
 */
const sopRejection = async (task, outcome, meta) => {
    if (outcome !== 'done' || task.verification?.requireSopSteps !== true) return null;
    const sop = (await loadArticleSteps())((task.knowledgeArticleId || '').trim());
    if (!sop) return null;
    const ticked = new Set(meta?.sopSteps || []);
    const missing = sop.steps.filter((s) => s.required && !ticked.has(s.id)).map((s) => s.title);
    if (!missing.length) return null;
    const error = new Error('Finish the SOP steps first.');
    error.code = 'E_TASK_SOP_INCOMPLETE';
    error.details = { missing, articleId: sop.articleId };
    error.isNetwork = false;
    return error;
};

/**
 * Same rules as the server for a verification-aware caller (one that sends
 * meta): `meta.undo` un-completes, the same outcome again is the idempotent
 * double tap answered with `alreadyResolved`, and a different outcome
 * overwrites — a check-off never toggles itself off. A task a teammate resolved
 * is not mine to repeat or undo: the server records my completion over theirs,
 * which is what "Mark done too" on a pool step does. Only a meta-less call, the
 * pre-verification contract, still toggles.
 *
 * Who/when are stamped so the mock shift flow can show "Done by Alex 7:52pm".
 * A done completion of a photo task also returns `completionVerification` and
 * `evidence`, the way TaskEvidence.attach does.
 */
export const mockMarkCompleted = async (id, taskId, outcome = 'done', reason = '', meta = null) => {
    const t = getStore().find((x) => x.taskId === taskId);
    if (!t) return delay({ ok: true });
    const resolvedByMe = t.status === 'Completed' && (t.completedBy || '') === me();
    const undo = Boolean(meta?.undo);
    if (undo) {
        if (resolvedByMe) {
            t.status = 'New';
            t.completionOutcome = '';
            t.completedBy = '';
            t.completedAt = '';
            t.completionReason = '';
            delete t.membersGone;
        }
        return delay({ ...t });
    }
    // The trigger for the checklist and the photo is the transition to done,
    // not "not resolved yet": Not needed corrected to Done still owes both.
    const priorOutcome = resolvedByMe ? (t.completionOutcome || 'done') : null;
    if (outcome === 'done' && priorOutcome !== 'done') {
        const rejection = await sopRejection(t, outcome, meta);
        if (rejection) throw rejection;
    }
    if (resolvedByMe && !meta) {
        t.status = 'New';
        t.completionOutcome = '';
        t.completedBy = '';
        t.completedAt = '';
        return delay({ ...t });
    }
    if (priorOutcome === outcome) return delay({ ...t, alreadyResolved: true });
    t.status = 'Completed';
    t.completionOutcome = ['not_needed', 'blocked'].includes(outcome) ? outcome : 'done';
    t.completedBy = me();
    t.completedAt = new Date().toISOString();
    // Echoed so an offline walk can show what the audit will read back: the
    // trainer's skip reason, and their answer to "Members already gone?".
    t.completionReason = reason || '';
    if (typeof meta?.membersGone === 'boolean') t.membersGone = meta.membersGone;
    const result = { ...t };
    if (t.completionOutcome === 'done' && t.verification?.method === 'photo') {
        result.completionVerification = mockVerification(t, meta);
        if (meta?.photoKey) {
            result.evidence = {
                photoKey: meta.photoKey,
                sha256: Math.random().toString(16).slice(2).padEnd(64, '0'),
                bytes: 184320,
                uploadedAt: t.completedAt,
            };
            // EXIF capture time, when the original file had one; the audit's
            // photo_stale rule reads it back off the stored evidence.
            if (meta.photoTakenAt) result.evidence.takenAt = meta.photoTakenAt;
        }
    }
    return delay(result);
};

export const mockAddTask = (username, newTask) => {
    const task = {
        taskId: newTaskId(),
        id: username,
        taskName: newTask.taskName,
        description: newTask.description,
        startDate: new Date().toISOString().slice(0, 10),
        dueDate: newTask.dueDate,
        assignedTo: newTask.assignedTo,
        assignedBy: newTask.assignedBy,
        status: 'New',
        priority: newTask.priority,
        recurrence: newTask.recurrence === 'none' ? null : newTask.recurrence,
        shiftPhase: newTask.shiftPhase || '',
        requirementLevel: newTask.requirementLevel || 'required',
        knowledgeArticleId: newTask.knowledgeArticleId || '',
        verification: normalizeVerification(newTask.verification),
        flowOrder: parseFlowOrder(newTask.flowOrder),
        flowKind: parseFlowKind(newTask.flowKind),
    };
    getStore().unshift(task);
    return delay({ ok: true, task });
};

export const mockUpdateTask = (id, taskId, updates) => {
    const t = getStore().find((x) => x.taskId === taskId);
    if (t) {
        const { verification: nextVerification, flowOrder, flowKind, ...rest } = updates || {};
        Object.assign(t, rest);
        if (nextVerification !== undefined) t.verification = normalizeVerification(nextVerification);
        if (flowOrder !== undefined) t.flowOrder = parseFlowOrder(flowOrder);
        if (flowKind !== undefined) t.flowKind = parseFlowKind(flowKind);
    }
    return delay({ ok: true });
};

export const mockDeleteTask = (id, taskId) => {
    store = getStore().filter((x) => x.taskId !== taskId);
    return delay({ ok: true });
};

// ---------------------------------------------------------------------------
// Member testimonials (MemberTestimonials.py). These live in localStorage per
// gym and business day rather than in the in-memory store, so an offline demo
// survives a reload the way the server does. TaskUtils passes the business date
// in; reading it here would close an import cycle back through TaskUtils.
// ---------------------------------------------------------------------------

export const TESTIMONIAL_STORE_PREFIX = 'bbvr_mock_testimonials';

const testimonialKey = (gymId, businessDate) => `${TESTIMONIAL_STORE_PREFIX}:${gymId}:${businessDate}`;

const readTestimonials = (gymId, businessDate) => {
    try {
        const raw = JSON.parse(localStorage.getItem(testimonialKey(gymId, businessDate)) || '[]');
        return Array.isArray(raw) ? raw : [];
    } catch (e) {
        return [];
    }
};

const writeTestimonials = (gymId, businessDate, list) => {
    try {
        localStorage.setItem(testimonialKey(gymId, businessDate), JSON.stringify(list));
    } catch (e) {
        /* ignore storage failures */
    }
};

const invalidTestimonial = (field) => {
    const error = new Error('That testimonial is missing something.');
    error.code = 'E_TESTIMONIAL_INVALID';
    error.details = { field };
    error.isNetwork = false;
    return error;
};

const HTTP_URL = /^https?:\/\/\S+$/i;
const QUOTE_MAX = 1000;
const NOTES_MAX = 500;

/** Field-by-field, the same order MemberTestimonials.add validates in. */
const testimonialRejection = (entry) => {
    if (!String(entry?.memberName || '').trim()) return invalidTestimonial('memberName');
    if (!['quote', 'video'].includes(entry?.kind)) return invalidTestimonial('kind');
    const content = String(entry?.content || '').trim();
    if (!content) return invalidTestimonial('content');
    if (entry.kind === 'video' ? !HTTP_URL.test(content) : content.length > QUOTE_MAX) {
        return invalidTestimonial('content');
    }
    if (String(entry?.notes || '').trim().length > NOTES_MAX) return invalidTestimonial('notes');
    return null;
};

const countMine = (list) => list.filter((t) => t.trainer === me()).length;

/**
 * Recompute the testimonial task's count from the saved list instead of
 * incrementing it: the task store resets on reload and localStorage does not,
 * so a blind increment would drift. Reaching the target checks the task off,
 * which is what the server does before it answers `taskCompleted`.
 */
const syncTestimonialTask = (list) => {
    const count = countMine(list);
    const task = getStore().find((t) => t.verification?.method === 'testimonial');
    if (!task) return { count, target: TESTIMONIAL_TARGET, justCompleted: false };
    const target = Number(task.testimonialTarget) || TESTIMONIAL_TARGET;
    task.testimonialCount = count;
    const justCompleted = count >= target && task.status !== 'Completed';
    if (justCompleted) {
        task.status = 'Completed';
        task.completionOutcome = 'done';
        task.completedBy = me();
        task.completedAt = new Date().toISOString();
    }
    return { count, target, justCompleted };
};

export const mockAddMemberTestimonial = (gymId, entry, businessDate) => {
    const rejection = testimonialRejection(entry);
    if (rejection) return Promise.reject(rejection);
    const testimonial = {
        id: `mock-tst-${Date.now()}-${(seq += 1)}`,
        gymId: String(gymId),
        businessDate,
        trainer: me(),
        memberName: String(entry.memberName).trim(),
        kind: entry.kind,
        content: String(entry.content).trim(),
        notes: String(entry.notes || '').trim(),
        consent: Boolean(entry.consent),
        createdAt: new Date().toISOString(),
    };
    const list = [...readTestimonials(gymId, businessDate), testimonial];
    writeTestimonials(gymId, businessDate, list);
    const { count, target, justCompleted } = syncTestimonialTask(list);
    return delay({ testimonial, todayCount: count, target, taskCompleted: justCompleted, slackPosted: true });
};

export const mockListMemberTestimonials = (gymId, businessDate) => {
    const list = readTestimonials(gymId, businessDate);
    const { count, target } = syncTestimonialTask(list);
    const byTrainer = {};
    list.forEach((t) => { byTrainer[t.trainer] = (byTrainer[t.trainer] || 0) + 1; });
    return delay({ date: businessDate, gymId: String(gymId), target, testimonials: list, byTrainer, mine: count });
};

const CSV_COLUMNS = ['date', 'trainer', 'member', 'kind', 'content', 'notes', 'consent'];

/**
 * Byte-for-byte what the server writes, because a manager comparing a mock
 * export against a real one should see the same file: an apostrophe in front of
 * anything a spreadsheet would evaluate as a formula, quotes only where the
 * value needs them, and CRLF between records as RFC 4180 asks.
 */
const csvCell = (value) => {
    const text = String(value ?? '');
    const guarded = /^[=+\-@]/.test(text) ? `'${text}` : text;
    return /["\n\r,]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

const csvRow = (t) => [
    t.businessDate, t.trainer, t.memberName, t.kind, t.content, t.notes, t.consent ? 'true' : 'false',
].map(csvCell).join(',');

const eachDay = (startDate, endDate, visit) => {
    const [y, m, d] = startDate.split('-').map(Number);
    for (let day = new Date(Date.UTC(y, m - 1, d)); ; day.setUTCDate(day.getUTCDate() + 1)) {
        const iso = day.toISOString().slice(0, 10);
        if (iso > endDate) return;
        visit(iso);
    }
};

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Range export the manager Review tab offers. The rows live one localStorage
 * key per business day, so the range is walked day by day rather than scanned.
 */
export const mockExportMemberTestimonials = (gymId, startDate, endDate) => {
    if (!DAY_RE.test(String(startDate)) || !DAY_RE.test(String(endDate))) {
        return Promise.reject(new Error('Dates must be YYYY-MM-DD.'));
    }
    if (endDate < startDate) return Promise.reject(new Error('endDate must be on or after date.'));
    const rows = [];
    eachDay(startDate, endDate, (day) => rows.push(...readTestimonials(gymId, day)));
    const csv = [CSV_COLUMNS.join(','), ...rows.map(csvRow)].join('\r\n');
    return delay({ csv, count: rows.length, consentedCount: rows.filter((t) => t.consent).length });
};

export const mockFetchEmployees = () =>
    delay([
        { label: me(), value: me() },
        { label: 'Alex Rivera', value: 'Alex Rivera' },
        { label: 'Priya Patel', value: 'Priya Patel' },
        { label: 'Maria Gomez', value: 'Maria Gomez' },
        { label: 'Devin Brooks', value: 'Devin Brooks' },
        { label: 'On Shift Staff', value: 'On Shift Staff' },
    ]);
